import PropTypes from 'prop-types';
import { memo, useCallback, useEffect } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';

import Topics from './Topics';
import TopicsBreadcrumb from './TopicsBreadcrumb';
import { LANE } from '../constants/displayAs';
import { CATEGORY } from '../constants/types';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import useTopicsCategory from '../hooks/useTopicsCategory';

const TopicsCategory = ({ category, displayAs = LANE }) => {
  const {
    handleFetchTopics,
    isFetchingTopics,
    loadNextPage,
    name,
    params,
    topics,
    totalTopics,
    updateTopicApproval,
  } = useTopicsCategory({ category });

  const {
    currentView,
    pushHandleFetchCategoryTopics,
    selectedSuperCategoryId,
    setCategoryView,
  } = useTopicsIndexContext();

  useEffect(() => {
    if (handleFetchTopics && pushHandleFetchCategoryTopics) {
      pushHandleFetchCategoryTopics(category.id, handleFetchTopics);
    }
  }, [handleFetchTopics, pushHandleFetchCategoryTopics]);

  const onSeeAll = useCallback(() => {
    setCategoryView(category.id, selectedSuperCategoryId);
  }, [category.id]);

  const isFetching = !category.id || isFetchingTopics;
  const isEmptyCategory =
    currentView === CATEGORY &&
    totalTopics === 0 &&
    category.id &&
    !isFetchingTopics;

  return (
    <>
      <Topics
        dataTestId="topics-category"
        displayAs={displayAs}
        isFetchingTopics={isFetching}
        loadNextPage={loadNextPage}
        name={name}
        onSeeAll={onSeeAll}
        params={params}
        topics={topics}
        totalTopics={totalTopics}
        updateTopicApproval={updateTopicApproval}
      />

      {isEmptyCategory && (
        <Grid>
          <Row mb={8}>
            <Col width={6 / 12}>
              <TopicsBreadcrumb
                displayAs={displayAs}
                isTopicLoadingCards={isFetching || topics.length === 0}
                name={name}
                onSeeAll={onSeeAll}
                totalTopics={totalTopics}
              />
              <Card mt={8}>
                <Box display="block" font="h4-regular">
                  {s__('Topics|No groups found in this category')}
                </Box>
              </Card>
            </Col>
          </Row>
        </Grid>
      )}
    </>
  );
};

TopicsCategory.protoTypes = {
  category: PropTypes.object.isRequired,
  displayAs: PropTypes.string,
};

export default memo(
  TopicsCategory,
  (prev, next) =>
    prev.category?.id === next.category?.id &&
    prev.displayAs === next.displayAs,
);
